import { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { getAdminAssessments, deleteAssessment, downloadAssessmentPDF } from "@/lib/api";
import { Trash2, Loader2, BarChart3, Eye, Download } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer } from "recharts";

const DEPT_LABELS = {
  finance: "Finance", procurement: "Procurement", inventory: "Inventory",
  production: "Production", sales: "Sales", hr: "HR", quality: "Quality", supply_chain: "Supply Chain"
};

export default function AdminAssessments() {
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try { const res = await getAdminAssessments(); setAssessments(res.data); }
    catch { toast.error("Failed to load assessments"); }
    finally { setLoading(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this assessment?")) return;
    try { await deleteAssessment(id); setAssessments(a => a.filter(x => x.id !== id)); toast.success("Deleted"); }
    catch { toast.error("Failed to delete"); }
  };

  const handleDownloadPDF = async (a) => {
    try {
      toast.info("Generating PDF...");
      const res = await downloadAssessmentPDF(a.id);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Assessment_${a.company_name.replace(/\s+/g, '_')}_${a.id.slice(0, 8)}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded!");
    } catch { toast.error("Failed to download PDF"); }
  };

  const avgScore = assessments.length > 0 ? (assessments.reduce((s, a) => s + a.overall_score, 0) / assessments.length).toFixed(1) : "0";

  return (
    <AdminLayout title="Digital Maturity Assessments">
      <div className="grid sm:grid-cols-3 gap-4 mb-6">
        <Card><CardContent className="p-4 text-center"><div className="text-2xl font-bold text-[#0066CC]">{assessments.length}</div><div className="text-xs text-slate-500">Total Assessments</div></CardContent></Card>
        <Card><CardContent className="p-4 text-center"><div className="text-2xl font-bold text-green-600">{avgScore}</div><div className="text-xs text-slate-500">Avg Maturity Score</div></CardContent></Card>
        <Card><CardContent className="p-4 text-center"><div className="text-2xl font-bold text-amber-600">{assessments.filter(a => a.overall_score < 2.5).length}</div><div className="text-xs text-slate-500">Low Maturity Leads</div></CardContent></Card>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" /></div>
          ) : assessments.length === 0 ? (
            <div className="p-8 text-center text-slate-400"><BarChart3 className="w-8 h-8 mx-auto mb-2" />No assessments yet</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Company</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Industry</TableHead>
                  <TableHead>Score</TableHead>
                  <TableHead>Level</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {assessments.map(a => (
                  <TableRow key={a.id}>
                    <TableCell className="font-medium">{a.company_name}</TableCell>
                    <TableCell><div className="text-sm">{a.contact_name}</div><div className="text-xs text-slate-400">{a.contact_email}</div></TableCell>
                    <TableCell className="text-sm">{a.industry || "-"}</TableCell>
                    <TableCell><span className={`font-bold ${a.overall_score >= 3.5 ? "text-green-600" : a.overall_score >= 2.5 ? "text-amber-600" : "text-red-600"}`}>{a.overall_score}/5</span></TableCell>
                    <TableCell className="text-xs">{a.maturity_level}</TableCell>
                    <TableCell className="text-xs text-slate-500">{new Date(a.created_at).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => setSelected(a)} data-testid={`view-assessment-${a.id}`}><Eye className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDownloadPDF(a)} data-testid={`download-assessment-pdf-${a.id}`} title="Download PDF"><Download className="w-4 h-4 text-[#0066CC]" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(a.id)} className="text-red-500 hover:text-red-700"><Trash2 className="w-4 h-4" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{selected?.company_name} — Maturity Report</DialogTitle></DialogHeader>
          {selected && (
            <div className="space-y-4">
              <div className="text-center p-4 bg-blue-50 rounded-lg">
                <div className="text-3xl font-bold text-[#0066CC]">{selected.overall_score}/5</div>
                <div className="text-sm text-slate-600">{selected.maturity_level}</div>
              </div>
              <div className="h-[280px]">
                <ResponsiveContainer width="100%" height="100%">
                  <RadarChart data={Object.entries(selected.department_scores || {}).map(([k, v]) => ({
                    dept: DEPT_LABELS[k] || k, score: v, benchmark: selected.benchmarks?.[k] || 3
                  }))}>
                    <PolarGrid /><PolarAngleAxis dataKey="dept" tick={{ fontSize: 10 }} /><PolarRadiusAxis domain={[0, 5]} />
                    <Radar name="Score" dataKey="score" stroke="#0066CC" fill="#0066CC" fillOpacity={0.2} />
                    <Radar name="Benchmark" dataKey="benchmark" stroke="#10B981" fill="none" strokeDasharray="5 5" />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {Object.entries(selected.department_scores || {}).map(([k, v]) => (
                  <div key={k} className="flex justify-between p-2 bg-slate-50 rounded text-sm">
                    <span className="capitalize">{DEPT_LABELS[k] || k}</span>
                    <span className="font-bold">{v}/5</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
